"use client";

import { useCallback, useEffect, useState } from "react";

const ED = {
  font: 'var(--font-geist-sans), system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  text: "#0c4a6e",
  muted: "#64748b",
  zOverlay: 10050,
};

/**
 * First-visit spotlight: grey mask, clear “hole”, speech-bubble copy.
 * Exit only via ✕. Advance via in-bubble text link (not a “Next” button).
 */
export function DashboardTourOverlay({ steps, onDismiss }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  const refreshRect = useCallback(() => {
    const sel = steps[step]?.target;
    if (!sel || typeof document === "undefined") {
      setRect(null);
      return;
    }
    const el = document.querySelector(sel);
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
    });
  }, [step, steps]);

  useEffect(() => {
    queueMicrotask(() => {
      refreshRect();
    });
    const t = window.setTimeout(refreshRect, 380);
    const onResize = () => refreshRect();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [refreshRect]);

  const s = steps[step];
  const pad = 10;
  const last = step >= steps.length - 1;

  if (!s) return null;

  const x = rect ? Math.max(0, rect.left - pad) : 0;
  const y = rect ? Math.max(0, rect.top - pad) : 0;
  const w = rect ? rect.width + pad * 2 : 0;
  const h = rect ? rect.height + pad * 2 : 0;
  const vw = typeof window !== "undefined" ? window.innerWidth : 0;
  const vh = typeof window !== "undefined" ? window.innerHeight : 0;

  const holeCenterY = rect ? y + h / 2 : vh / 2;
  const bubbleOnTop = rect ? holeCenterY > vh * 0.55 : false;

  let bubbleLeft = "50%";
  let bubbleTransform = "translateX(-50%)";
  if (rect && vw > 400) {
    const cx = x + w / 2;
    const pct = Math.min(88, Math.max(12, (cx / vw) * 100));
    bubbleLeft = `${pct}%`;
    bubbleTransform = "translateX(-50%)";
  }

  const dim = "rgba(15, 23, 42, 0.74)";

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: ED.zOverlay, fontFamily: ED.font }} role="dialog" aria-modal="true" aria-labelledby="ed-tour-title" aria-describedby="ed-tour-desc">
      {rect && vw > 0 ? (
        <>
          <div style={{ position: "fixed", left: 0, top: 0, right: 0, height: y, background: dim }} aria-hidden />
          <div style={{ position: "fixed", left: 0, top: y, width: x, height: h, background: dim }} aria-hidden />
          <div style={{ position: "fixed", left: x + w, top: y, right: 0, height: h, background: dim }} aria-hidden />
          <div style={{ position: "fixed", left: 0, top: y + h, right: 0, bottom: 0, background: dim }} aria-hidden />
        </>
      ) : (
        <div style={{ position: "fixed", inset: 0, background: dim }} aria-hidden />
      )}

      {/* Soft border around the “clear” area */}
      {rect && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            left: x,
            top: y,
            width: w,
            height: h,
            borderRadius: 14,
            boxShadow: "0 0 0 3px rgba(56, 189, 248, 0.65), 0 0 28px rgba(56, 189, 248, 0.35)",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Pointer cue */}
      {rect && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            left: x + w * 0.72,
            top: y + h * 0.15,
            fontSize: 28,
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.25))",
            pointerEvents: "none",
            zIndex: ED.zOverlay + 1,
          }}
        >
          👆
        </div>
      )}

      <button
        type="button"
        onClick={onDismiss}
        aria-label="Close tour"
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: ED.zOverlay + 3,
          width: 44,
          height: 44,
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.35)",
          background: "rgba(255,255,255,0.95)",
          color: ED.text,
          fontSize: "1.35rem",
          fontWeight: 700,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 8px 28px rgba(15,23,42,0.18)",
          lineHeight: 1,
        }}
      >
        ×
      </button>

      <div
        id="ed-tour-desc"
        style={{
          position: "fixed",
          left: bubbleLeft,
          top: bubbleOnTop ? "4.5rem" : undefined,
          bottom: bubbleOnTop ? undefined : "5rem",
          transform: bubbleTransform,
          maxWidth: "min(420px, calc(100vw - 2rem))",
          zIndex: ED.zOverlay + 2,
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          borderRadius: 18,
          padding: "1.15rem 1.25rem",
          boxShadow: "0 18px 48px rgba(15,23,42,0.2), 0 0 0 1px rgba(148,163,184,0.2)",
          border: "1px solid rgba(226,232,240,0.9)",
        }}
      >
        <p style={{ margin: "0 0 0.2rem", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.14em", color: "#94a3b8", textTransform: "uppercase" }}>Quick tour</p>
        <h2 id="ed-tour-title" style={{ margin: "0 0 0.55rem", fontSize: "1.125rem", fontWeight: 700, color: ED.text, letterSpacing: "-0.02em", lineHeight: 1.25 }}>
          {s.title}
        </h2>
        <p style={{ margin: 0, fontSize: "0.9375rem", lineHeight: 1.55, color: ED.muted }}>{s.body}</p>
        {!last ? (
          <span
            role="button"
            tabIndex={0}
            onClick={() => setStep((i) => Math.min(i + 1, steps.length - 1))}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setStep((i) => Math.min(i + 1, steps.length - 1));
              }
            }}
            style={{
              display: "inline-block",
              marginTop: "0.95rem",
              color: "#0284c7",
              fontWeight: 600,
              fontSize: "0.875rem",
              cursor: "pointer",
              textDecoration: "underline",
              textUnderlineOffset: "3px",
              fontFamily: ED.font,
            }}
          >
            See another tip →
          </span>
        ) : (
          <p style={{ margin: "0.85rem 0 0", fontSize: "0.8125rem", color: "#94a3b8", lineHeight: 1.45 }}>
            When you’re ready, tap <strong style={{ color: ED.text }}>×</strong> and explore. Open <strong style={{ color: ED.text }}>How to use</strong> anytime for the full guide.
          </p>
        )}
      </div>
    </div>
  );
}

export const DASHBOARD_TOUR_STEPS = [
  {
    target: '[data-tour="welcome"]',
    title: "Welcome to your dashboard",
    body: "You’re signed in and looking at your live list. Everything you add stays tied to this account.",
  },
  {
    target: '[data-tour="dashboard-view-mode"]',
    title: "Finance or stock-first",
    body: "Use Finance desk for Rand totals and line values. Switch to Stock & counts to focus on bottles, boxes, and quantities with less money on screen — your choice is remembered.",
  },
  {
    target: '[data-tour="stats"]',
    title: "Three numbers worth watching",
    body: "Top tiles track write-offs, risk, and recovered stock. In Finance desk they show money; in Stock & counts they show lines and quantity totals.",
  },
  {
    target: '[data-tour="upload-actions"]',
    title: "From paper to rows",
    body: "Add sheet reads a printout or PDF. Batch scan is for staff on the floor: scan every item in the dispensary, then Submit to sync. Both feed the list below.",
  },
  {
    target: '[data-tour="filters"]',
    title: "Find what matters first",
    body: "Filter by status, then sort by urgency, value, or name so the next task is obvious.",
  },
  {
    target: '[data-tour="profile-menu-btn"]',
    title: "Help & settings live under Account",
    body: "Open Account for How to use, Settings, batch scan, billing, and sign out. The sun or moon below toggles light or dark theme for calmer evening shifts.",
  },
];
